import React, { useEffect, useState } from "react";
import {
    Button, Card, Table, Drawer, Form, Input, Select,
    InputNumber, Switch, Space, AutoComplete, Tag, Popconfirm, Empty,
} from "antd";
import { GeneralService } from "../../Services/GeneralService";
import { notify } from "../../components/Customs/NotifyProvider";
import { formatDate } from "../../components/Customs/dateFormatter";
import TitleHead from "../../components/Customs/TitleHead";


import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from "../../../utils/permissionUtils";

const MODULE_KEY = "inhouse_services";

const InHouseServices = () => {
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();
    const [data, setData] = useState([]);
    const categoryOptions = [
        { value: "Food" },
        { value: "Laundry" },
        { value: "Transport" },
        { value: "Housekeeping" },
        { value: "Luggage Handling" },
        { value: "Taxi / Pickup" },
        { value: "AC / Heater Charges" },
        { value: "Extra Bed" },
        { value: "Other" },
    ];
    const [isEditing, setIsEditing] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    const [options, setOptions] = useState(categoryOptions);
    const [searchText, setSearchText] = useState("");

    const getAllServices = async () => {
        try {
            const res = await GeneralService.get("inhouse_services");
            console.log("res.data", res.data);

            setData(
                (res.data || []).map((item, index) => ({
                    si_no: index + 1,
                    ...item,
                }))
            );
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        getAllServices();
    }, [])

    const filteredData = data.filter((item) => {
        const value = searchText.toLowerCase();

        return Object.values(item).some((field) => {
            if (field === null || field === undefined) return false;
            return field.toString().toLowerCase().includes(value);
        });
    });

    const handleSearch = (value) => {
        if (!value) {
            setOptions(categoryOptions);
        } else {
            const filtered = categoryOptions.filter((item) =>
                item.value.toLowerCase().includes(value.toLowerCase())
            );
            setOptions(filtered);
        }
    };

    // Table Columns
    const columns = [
        {
            title: "S. No.",
            dataIndex: "si_no",
            align: "center",
        },
        {
            title: "Service Name",
            dataIndex: "name",
        },
        {
            title: "Category",
            dataIndex: "category",
        },
        {
            title: "Price",
            dataIndex: "price",
        },
        {
            title: "Type",
            dataIndex: "pricing_type",
        },
        {
            title: "Unit",
            dataIndex: "unit",
        },
        {
            title: "Active",
            dataIndex: "is_active",
            render: (val) => (val ? <Tag color="green">Active</Tag> : <Tag color="red">In-Active</Tag>),
        },
        {
            title: "Actions",
            render: (_, record) => (
                <Space>
                    {canUpdateModule(MODULE_KEY) && (
                        <Button type="link" onClick={() => handleEdit(record)}>
                            Edit
                        </Button>
                    )}
                    {canDeleteModule(MODULE_KEY) && (
                        <Popconfirm
                            title="Delete Record"
                            description="Are you sure you want to delete this item?"
                            okText="Yes"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(record)}
                        >
                            <Button danger type="link">
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        }
    ];

    const handleEdit = (record) => {
        setIsEditing(true);
        setEditingRecord(record);
        setOpen(true);

        form.setFieldsValue({
            ...record,
        });
    };

    const handleDelete = async (record) => {
        try {
            const res = await GeneralService.delete("inhouse_services", record.id);

            if (res.status) {
                notify.success("Service deleted");
                setData((prev) => prev.filter((item) => item.id !== record.id));
            } else {
                notify.error("Delete failed");
            }
        } catch (err) {
            console.error(err);
        }
    };

    // Open Drawer
    const showDrawer = () => {
        setOpen(true);
    };

    // Close Drawer
    const onClose = () => {
        setOpen(false);
        form.resetFields();
        setIsEditing(false);
        setEditingRecord(null);
    };

    // Save Form
    const onFinish = async (values) => {
        const newData = {
            ...values,
            updated_at: formatDate(),
        };

        try {
            if (isEditing) {
                // UPDATE
                const req = await GeneralService.update(
                    "inhouse_services",
                    editingRecord.id,
                    newData

                );

                if (req.status) {
                    notify.success("Service updated successfully");

                    getAllServices();

                    setIsEditing(false);
                    setEditingRecord(null);
                    onClose();
                } else {
                    notify.error("Update failed");
                    getAllServices();
                }
            } else {
                // CREATE
                const req = await GeneralService.create("inhouse_services", {
                    ...newData,
                    created_at: formatDate(),
                });

                if (req.status) {
                    notify.success("Service created successfully");
                    setData((prev) => [...prev, req.data]);
                    onClose();
                    getAllServices();
                } else {
                    notify.error("Create failed");
                    getAllServices();
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    if (!canViewModule(MODULE_KEY)) {
        return <Empty description="No permission to view this page" />;
    }

    return (
        <Card
            title={
                <TitleHead h1='In-House Services' h2='Guest service usage, internal service requests, and billing overview.' />
            }
            extra={
                <Space>
                    <Input.Search
                        size="large"
                        placeholder="Search services..."
                        allowClear
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 340 }}
                    />

                    {canCreateModule(MODULE_KEY) && (
                        <Button size="large" type="primary" onClick={showDrawer}>
                            Create Services
                        </Button>
                    )}
                </Space>
            }
        >
            <Table columns={columns} dataSource={filteredData} pagination={{
                showSizeChanger: true,
                // showQuickJumper: true,
                showTotal: (total) => `Total ${total} records`,
            }} />

            {/* Drawer */}
            <Drawer
                title={isEditing ? "Edit Service" : "Create Service"}
                width={400}
                open={open}
                onClose={onClose}
            >
                <Form layout="vertical" form={form} onFinish={onFinish}>
                    <Form.Item
                        label="Service Name"
                        name="name"
                        rules={[{ required: true, message: "Enter service name" }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item label="Category" name="category">
                        <AutoComplete
                            options={options}
                            onSearch={handleSearch}
                            placeholder="Type or select category"
                            filterOption={false}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Price"
                        name="price"
                    >
                        <InputNumber style={{ width: "100%" }} />
                    </Form.Item>

                    <Form.Item label="Pricing Type" name="pricing_type" initialValue="fixed">
                        <Select
                            options={[
                                { label: "Fixed", value: "fixed" },
                                { label: "Per Unit", value: "per_unit" },
                                { label: "Per Day", value: "per_day" },
                            ]}
                        />
                    </Form.Item>

                    <Form.Item label="Unit" name="unit">
                        <Input placeholder="kg / item / hour" />
                    </Form.Item>

                    <Form.Item
                        label="Active"
                        name="is_active"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item label="Description" name="description">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Space>
                        <Button type="primary" htmlType="submit">
                            Save
                        </Button>
                        <Button onClick={onClose}>Cancel</Button>
                    </Space>
                </Form>
            </Drawer>
        </Card>
    );
};

export default InHouseServices;